/**
 * Session orchestration hook (WS6): replay-once + send/stop wiring.
 *
 * Replay happens exactly once per session id (the mark lives in chatStore
 * and is kept by the stream controller) so layout changes that unmount and
 * remount the transcript column never re-fold the session's diagrams or wipe
 * a live buffer mid-turn. A transport drop forgets the mark: the turn is
 * picked up again from the server (api.md §7, 2026-09-07), and when that
 * fails too the refetch that follows rebuilds the canonical state from
 * display_json.
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';

import { useSession } from '../../api/queries';
import type { MessageFocus, MessageMention } from '../../api/sse';
import type { ChatSessionDetail, Ulid } from '../../api/types';
import { liveFor, useChatStore } from '../../stores/chatStore';
import {
  canStartTurn,
  hasReplayed,
  isTurnActiveFor,
  markReplayed,
  startTurn,
  stopTurn,
} from './streamController';

export interface ChatSessionApi {
  session: UseQueryResult<ChatSessionDetail>;
  /**
   * Send one turn. `focus` is what the question is about when it came from
   * parts picked out of the diagram (round 8); `mentions` what the reader
   * tagged (DECISIONS #95); a plain question sends neither. Returns whether
   * the text was taken: false when a turn is still registered for this
   * session, so the composer keeps the draft (2026-09-08).
   */
  send: (text: string, focus?: MessageFocus | null, mentions?: readonly MessageMention[]) => boolean;
  stop: () => void;
}

export function useChatSession(sessionId: Ulid): ChatSessionApi {
  const qc = useQueryClient();
  const session = useSession(sessionId);

  // NOTE (multi-stream): switching sessions aborts NOTHING — every other
  // session's stream keeps pumping into its own live buffer.

  // Replay writes ACTIVE-session state (the diagram fold, the pane's view)
  // — it must wait until the mounting page has committed this session as
  // the active one (the route-scoped SessionPage sets it in an effect that
  // runs AFTER children).
  const activeSessionId = useChatStore((s) => s.activeSessionId);

  const messages = session.data?.messages;
  useEffect(() => {
    if (activeSessionId !== sessionId) return;
    if (messages === undefined) return;
    if (hasReplayed(sessionId)) return;
    const live = liveFor(useChatStore.getState(), sessionId);
    if (isTurnActiveFor(sessionId) || live.streaming || live.frames.length > 0) {
      // The live stream IS the current state (a brand-new session whose
      // opener is in flight, or a turn that just finished) — replaying the
      // fetched history over it would wipe the live buffer mid-turn.
      markReplayed(sessionId);
      return;
    }
    useChatStore.getState().replaySession(messages);
    markReplayed(sessionId);
  }, [activeSessionId, sessionId, messages]);

  // Replay runs once; the session keeps being read (a turn lands, a poll
  // returns). The diagram pane folds from the SAME bodies the transcript
  // draws, so its source has to track every one of those reads. This touches
  // no live buffer and never moves the reader off the view they are on.
  useEffect(() => {
    if (activeSessionId !== sessionId) return;
    if (messages === undefined) return;
    if (!hasReplayed(sessionId)) return;
    useChatStore.getState().syncDiagramSource(sessionId, messages);
  }, [activeSessionId, sessionId, messages]);

  return {
    session,
    send: (text: string, focus?: MessageFocus | null, mentions?: readonly MessageMention[]) => {
      // Answered now, not when the turn settles: the composer clears or
      // keeps its draft on this, and a turn runs for minutes. startTurn
      // makes the same test first, in the same tick, so the two agree.
      if (!canStartTurn(sessionId)) return false;
      void startTurn(qc, sessionId, text, focus, mentions);
      return true;
    },
    stop: () => stopTurn(sessionId),
  };
}
